import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, Instagram, Facebook, Youtube } from "lucide-react";
import { ASSETS, SITE, callLink, whatsappLink } from "../lib/constants";
import { SERVICES } from "../data/services";

export default function Footer() {
  return (
    <footer className="bg-slate-50 border-t border-slate-200 mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
              <div className="w-16 h-16 flex items-center justify-center -my-2">
                <img src={ASSETS.logo} alt="Lotus Family Dental" className="w-full h-full object-contain drop-shadow-sm" />
              </div>
              <div>
                <div className="font-heading text-base font-semibold text-slate-900">
                  Lotus Family Dental
                </div>
                <div className="text-xs uppercase tracking-wider text-teal-600 font-medium">
                  Smile Specialists
                </div>
              </div>
            </Link>
            <p className="mt-5 text-sm text-slate-600 leading-relaxed max-w-xs">
              The most trusted dental clinic in Allahabad (Prayagraj) — offering painless, advanced
              dental care with global standards.
            </p>
            <div className="flex gap-3 mt-5">
              <a
                href={SITE.social.instagram}
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={SITE.social.facebook}
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={SITE.social.youtube}
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-slate-900 mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Doctors" },
                { to: "/services", label: "Services" },
                { to: "/blog", label: "Blog" },
                { to: "/contact", label: "Contact" },
                { to: "/book-appointment", label: "Book Appointment" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-600 hover:text-teal-700 transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-slate-900 mb-4">Treatments</h4>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-slate-600 hover:text-teal-700 transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-slate-900 mb-4">Reach Us</h4>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{SITE.address}</span>
              </li>
              <li>
                <a
                  href={callLink}
                  className="flex items-start gap-3 hover:text-teal-700 transition-colors"
                >
                  <Phone className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>{SITE.phone}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{SITE.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>
                  {SITE.daysOpen}
                  <br />
                  {SITE.timings}
                </span>
              </li>
            </ul>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center mt-5 px-5 py-2.5 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors"
              data-testid="footer-whatsapp-button"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Lotus Family Dental, Prayagraj. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <span>Best Dental Clinic in Allahabad</span>
            <span>•</span>
            <span>Crafted with care</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
